import React from "react";
import { X } from "lucide-react";
import AudioPostPlayer from "./AudioPostPlayer";
import { Star, Heart } from "lucide-react";
import PostFormInputs from "./PostFormInputs";

/**
 * MediaCaptureSection - Media orchestrator component
 * Handles media preview display for images, videos, and audio
 */
const MediaCaptureSection = ({
	mediaPreview,
	mediaType,
	mediaFile,
	videoRef,
	recordingDuration,
	formatDuration,
	onRemoveMedia,
	selectedPostType,
	showPostPreview,
	// For video preview overlay
	content,
	onContentChange,
	onContentBlur,
	textareaRef,
	isListening,
	onStartSpeechRecognition,
	onStopSpeechRecognition,
	contestName,
	userName,
	isPosting,
	isRecording,
	currentLumens,
}) => {
	if (!mediaPreview) {
		return null;
	}

	// Audio preview
	if (mediaType === "audio") {
		return (
			<div className="relative mb-3 mt-2">
				<AudioPostPlayer
					audioUrl={mediaPreview}
					loadImmediately
					showStaticWaveform
				/>
				<button
					onClick={onRemoveMedia}
					className="absolute right-2 top-2 rounded-full bg-black bg-opacity-60 p-1 transition-all hover:bg-opacity-80"
					aria-label="Remove media"
				>
					<X size={16} className="text-white" />
				</button>
			</div>
		);
	}

	// Video preview - Fullscreen with inputs at bottom
	if (mediaType === "video" || mediaType === "video-recording") {
		return (
			<div className="relative mb-3 mt-2">
				<div className="relative mx-auto aspect-[9/16] w-full max-w-video-portrait overflow-hidden rounded-lg bg-black">
					<video
						key={mediaType === "video-recording" ? "recording" : "preview"}
						ref={mediaType === "video-recording" ? videoRef : undefined}
						className="absolute inset-0 h-full w-full object-contain"
						autoPlay
						muted
						loop
						playsInline
						controls={false}
						src={mediaType === "video" ? mediaPreview : undefined}
						onLoadedMetadata={(event) => {
							if (mediaType === "video-recording" && mediaPreview) {
								const videoElement = event.target;
								videoElement.play().catch(console.error);
							}
						}}
						onCanPlay={(event) => {
							if (mediaType === "video-recording" && mediaPreview) {
								const videoElement = event.target;
								if (videoElement.paused) {
									videoElement.play().catch(console.error);
								}
							}
						}}
					/>
					{/* Post Type Badge - Top Left */}
					{selectedPostType !== "pride" && (
						<div
							className={`absolute left-4 top-4 z-20 rounded-full px-4 py-2 text-sm font-bold ${
								selectedPostType === "grateful"
									? "bg-green-500 text-white"
									: "bg-yellow-500 text-white"
							}`}
						>
							{selectedPostType === "grateful" ? (
								<div className="flex items-center gap-2">
									<Heart size={16} />
									<span>Grateful</span>
								</div>
							) : (
								<div className="flex items-center gap-2">
									<Star size={16} />
									<span>Create Glow Post</span>
								</div>
							)}
						</div>
					)}
					{mediaType === "video-recording" && (
						<div className="absolute left-4 top-14 z-20 flex items-center rounded-md bg-red-500 px-3 py-1 text-sm text-white">
							<span className="mr-2 animate-pulse">●</span>
							Recording {formatDuration(recordingDuration)}
						</div>
					)}
					{/* Remove button */}
					<button
						onClick={onRemoveMedia}
						className="absolute right-4 top-4 z-20 rounded-full bg-black bg-opacity-60 p-2 transition-all hover:bg-opacity-80"
						aria-label="Remove media"
					>
						<X size={20} className="text-white" />
					</button>
					{/* Title and Body Inputs overlay at bottom */}
					{(mediaFile || mediaPreview || showPostPreview) &&
					!(selectedPostType === "grateful" && (currentLumens || 0) < 2) ? (
						<div className="absolute bottom-0 left-0 right-0 z-10 bg-gradient-to-t from-black/95 via-black/90 to-transparent p-4">
							<PostFormInputs
								content={content}
								setContent={onContentChange}
								onBlur={onContentBlur}
								textareaRef={textareaRef}
								isListening={isListening}
								onStartSpeechRecognition={onStartSpeechRecognition}
								onStopSpeechRecognition={onStopSpeechRecognition}
								selectedPostType={selectedPostType}
								contestName={contestName}
								userName={userName}
								isPosting={isPosting}
								isRecording={isRecording}
								showPostPreview={showPostPreview}
								currentLumens={currentLumens}
							/>
						</div>
					) : null}
				</div>
			</div>
		);
	}

	// Image preview - Square cropped with inputs below
	if (mediaType === "image") {
		return (
			<>
				<div className="relative aspect-square w-full overflow-hidden rounded-lg bg-black">
					<img
						src={mediaPreview}
						alt="Upload preview"
						className="h-full w-full object-cover"
					/>
					{/* Post Type Badge - Top Left */}
					{selectedPostType !== "pride" && (
						<div
							className={`absolute left-2 top-2 z-10 rounded-full px-4 py-2 text-sm font-bold ${
								selectedPostType === "grateful"
									? "bg-green-500 text-white"
									: "bg-yellow-500 text-white"
							}`}
						>
							{selectedPostType === "grateful" ? (
								<div className="flex items-center gap-2">
									<Heart size={16} />
									<span>Grateful</span>
								</div>
							) : (
								<div className="flex items-center gap-2">
									<Star size={16} />
									<span>Create Glow Post</span>
								</div>
							)}
						</div>
					)}
					{/* Remove button */}
					<button
						onClick={onRemoveMedia}
						className="absolute right-2 top-2 z-10 rounded-full bg-black bg-opacity-60 p-2 transition-all hover:bg-opacity-80"
						aria-label="Remove media"
					>
						<X size={20} className="text-white" />
					</button>
				</div>
				{/* Body Input below image */}
				{(mediaFile || mediaPreview || showPostPreview) &&
				!(selectedPostType === "grateful" && (currentLumens || 0) < 2) ? (
					<div className="relative mb-2 mt-3">
						<PostFormInputs
							content={content}
							setContent={onContentChange}
							onBlur={onContentBlur}
							textareaRef={textareaRef}
							isListening={isListening}
							onStartSpeechRecognition={onStartSpeechRecognition}
							onStopSpeechRecognition={onStopSpeechRecognition}
							selectedPostType={selectedPostType}
							contestName={contestName}
							userName={userName}
							isPosting={isPosting}
							isRecording={isRecording}
							showPostPreview={showPostPreview}
							currentLumens={currentLumens}
						/>
					</div>
				) : null}
			</>
		);
	}

	return null;
};

export default MediaCaptureSection;
